// Buttons, pills, placeholders, live dot — ported from design's shared.jsx.
import Beacon from './Beacon.jsx';
import { PALETTE } from '../palette.js';

export const Button = ({ children, variant = 'primary', size = 'md', onClick, disabled, style = {}, ...rest }) => {
  const sizes = {
    sm: { padding: '8px 14px', fontSize: 13, borderRadius: 8 },
    md: { padding: '12px 20px', fontSize: 14, borderRadius: 10 },
    lg: { padding: '16px 28px', fontSize: 15, borderRadius: 12 },
  };
  const variants = {
    primary: { background: PALETTE.ink, color: '#FAF8F5', border: `1px solid ${PALETTE.ink}` },
    accent:  {
      background: 'var(--active-accent, #E06A2C)',
      color: 'var(--active-accent-contrast, #fff)',
      border: '1px solid var(--active-accent-deep, #B8501A)',
    },
    ghost:   { background: 'transparent', color: PALETTE.ink, border: `1px solid ${PALETTE.controlBorder}` },
    soft:    { background: PALETTE.surfaceAlt, color: PALETTE.ink, border: `1px solid ${PALETTE.controlBorder}` },
    danger:  { background: 'transparent', color: '#A33', border: '1px solid var(--active-danger-border, #E8C9C1)' },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...sizes[size], ...variants[variant],
      fontFamily: 'var(--active-font-ui, Inter, sans-serif)', letterSpacing: '-0.01em',
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1,
      display: 'inline-flex', alignItems: 'center', gap: 8, transition: 'all .15s ease',
      ...style,
      fontWeight: 'var(--button-font-weight, 400)',
    }} {...rest}>
      {children}
    </button>
  );
};

// OGC-445: `className` and the remaining props (aria-label, title) pass through
// so campaign status pills keep their `bl-campaign-state` contract classes.
export const Pill = ({ children, tone = 'neutral', style = {}, className, ...rest }) => {
  const tones = {
    neutral: { bg: PALETTE.surfaceAlt, fg: PALETTE.ink2, bd: PALETTE.line },
    accent:  {
      bg: 'var(--active-accent-tint, #FDF4EC)',
      fg: 'var(--active-accent-deep, #B8501A)',
      bd: 'var(--active-accent-soft, #FBE7D6)',
    },
    live:    { bg: PALETTE.successSoft, fg: PALETTE.success, bd: '#CFD9B4' },
    warn:    { bg: '#FBEFD6', fg: PALETTE.warn, bd: '#EBDCB2' },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span {...rest} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', fontSize: 11.5, fontWeight: 500,
      letterSpacing: '0.04em', textTransform: 'uppercase',
      background: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
      borderRadius: 999, fontFamily: 'var(--active-font-ui, Inter, sans-serif)',
      ...style,
    }} className={className}>
      {children}
    </span>
  );
};

// OGC-446 status chip: a pill-shaped toggle used as a filter (aria-pressed) or
// as one option of a radiogroup (role="radio" + aria-checked). Styling lives
// in theme.css `.bl-chip`; `tone` colours the pressed state per lead status and
// `size` picks the 38px toolbar height or the 34px detail setter.
export const Chip = ({
  children,
  pressed,
  checked,
  role,
  tone,
  size = 'toolbar',
  busy = false,
  className = '',
  ...rest
}) => (
  <button
    type="button"
    role={role}
    className={`bl-chip${className ? ` ${className}` : ''}`}
    data-tone={tone || undefined}
    data-size={size === 'setter' ? 'setter' : undefined}
    aria-pressed={pressed === undefined ? undefined : pressed ? 'true' : 'false'}
    aria-checked={checked === undefined ? undefined : checked ? 'true' : 'false'}
    aria-busy={busy ? 'true' : undefined}
    {...rest}
  >
    {busy ? <Beacon size="inline" className="is-current" delayMs={0} slowAfterMs={null} /> : null}
    {children}
  </button>
);

export const Placeholder = ({ label = 'image', ratio = '16/9', style = {} }) => (
  <div style={{
    aspectRatio: ratio,
    background: `repeating-linear-gradient(135deg, ${PALETTE.surfaceAlt} 0 10px, ${PALETTE.lineSoft} 10px 20px)`,
    border: `1px solid ${PALETTE.line}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: PALETTE.ink3,
    letterSpacing: '0.05em', textTransform: 'uppercase',
    ...style,
  }}>
    {label}
  </div>
);

export const LiveDot = ({ color = PALETTE.success }) => (
  <span style={{ position: 'relative', width: 8, height: 8, display: 'inline-block' }}>
    <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: color, animation: 'rai-pulse 2s ease-out infinite' }}/>
    <span style={{ position: 'absolute', inset: 2, borderRadius: '50%', background: color }}/>
  </span>
);

export const Card = ({ children, style = {} }) => (
  <div style={{
    background: PALETTE.surface,
    border: `1px solid ${PALETTE.line}`,
    borderRadius: 14,
    padding: 20,
    ...style,
  }}>
    {children}
  </div>
);
